import React from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const TA_URL = "https://www.tripadvisor.it/Restaurant_Review-g656615-d12568705-Reviews-Provalo_Spiedinificio-Fiumicino_Province_of_Rome_Lazio.html";

const StatCard = ({ value, label, subtitle, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ delay, duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
    className="relative p-6 md:p-7 rounded-2xl border border-[var(--pn-ta-green)]/25 bg-[rgba(14,61,49,0.25)] backdrop-blur-sm"
  >
    <div className="font-display font-light text-[42px] md:text-[56px] leading-none text-[var(--pn-milk)]">{value}</div>
    <div className="mt-2 text-[13px] tracking-[0.18em] uppercase text-[var(--pn-ta-green)] font-medium">{label}</div>
    {subtitle && <div className="mt-3 text-[13px] text-[var(--pn-cream)]/55 leading-relaxed font-light">{subtitle}</div>}
  </motion.div>
);

export const Recensioni = () => {
  const { t } = useT();
  const x = t.recensioni;
  return (
    <section
      className="relative section-pad section-pad-top-tight overflow-hidden"
      style={{
        background: "linear-gradient(180deg, var(--pn-black) 0%, #0A1814 35%, #0E2A22 55%, #0A1814 80%, var(--pn-black) 100%)",
      }}
      data-testid="recensioni-section"
    >
      <div className="absolute inset-0 grain pointer-events-none opacity-60" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] h-[60vw] max-w-[1100px] max-h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(0,175,135,0.10) 0%, transparent 60%)" }}
      />
      <div className="relative max-w-[1200px] mx-auto">
        <motion.span
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.8 }} className="eyebrow"
          style={{ color: "var(--pn-ta-green)" }}
        >
          {x.eyebrow}
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
          className="mt-5 font-display font-light text-[38px] sm:text-[54px] md:text-[78px] lg:text-[90px] leading-[1.0] tracking-[-0.015em] text-[var(--pn-milk)] max-w-[16ch]"
        >
          {x.title_a}
          <br />
          <em className="italic" style={{ color: "var(--pn-ta-green)" }}>{x.title_b}</em>{x.title_c}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: 0.25, duration: 0.9 }}
          className="mt-8 max-w-[600px] text-[16px] md:text-[18px] leading-[1.7] text-[var(--pn-cream)]/85 font-light"
        >
          {x.desc}
        </motion.p>

        <div className="mt-14 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          <StatCard
            value={<>{x.stat1_v}<span className="text-[var(--pn-ta-green)]">{x.stat1_v_big}</span></>}
            label={x.stat1_label} subtitle={x.stat1_sub} delay={0.1}
          />
          <StatCard
            value={<>4,8<span className="text-[var(--pn-ta-green)] text-[28px] md:text-[34px]">/5</span></>}
            label={x.stat2_label} subtitle={x.stat2_sub} delay={0.2}
          />
          <StatCard value={x.stat3_v} label={x.stat3_label} subtitle={x.stat3_sub} delay={0.3} />
          <StatCard value={x.stat4_v} label={x.stat4_label} subtitle={x.stat4_sub} delay={0.4} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.9 }}
          className="mt-14 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          <a href={TA_URL} target="_blank" rel="noopener noreferrer" className="cta-ta" data-testid="cta-tripadvisor">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12.006 4.295c-2.67 0-5.338.784-7.645 2.353H0l1.963 2.135a4.997 4.997 0 0 0 3.405 8.66c1.43 0 2.794-.598 3.76-1.65l1.882 2.046 1.88-2.044a5.085 5.085 0 0 0 .163.172 4.97 4.97 0 0 0 3.604 1.477 4.998 4.998 0 0 0 3.405-8.66L21.99 6.647h-4.327a13.638 13.638 0 0 0-7.657-2.353zm-.004 1.4c1.802 0 3.598.347 5.262.998-3.103.4-5.273 2.886-5.273 5.945 0-3.06-2.169-5.553-5.265-5.948a14.495 14.495 0 0 1 5.276-.996zM5.369 8.39a3.605 3.605 0 0 1 .002 7.209 3.605 3.605 0 0 1-.002-7.21zm13.27 0a3.604 3.604 0 1 1 .001 7.208 3.604 3.604 0 0 1-.001-7.209zm-13.27 1.677a1.926 1.926 0 1 0 0 3.852 1.926 1.926 0 0 0 0-3.852zm13.27 0a1.926 1.926 0 1 0 0 3.852 1.926 1.926 0 0 0 0-3.852z"/>
            </svg>
            <span>{x.ctaTA}</span>
            <ExternalLink size={15} strokeWidth={2} />
          </a>
          <p className="font-display italic text-[13px] md:text-[14px] text-[var(--pn-cream)]/45 max-w-[260px] text-center sm:text-left">
            {x.ctaCaption}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
